import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './Discover.css';

const TutorDiscovery = () => {
  const navigate = useNavigate();
  const searchInputRef = useRef(null);

  // ── STATE ──
  const [activeTab, setActiveTab] = useState('group');
  const [searchQuery, setSearchQuery] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [allProfiles, setAllProfiles] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [likesState, setLikesState] = useState({});
  const [joiningGroups, setJoiningGroups] = useState({});
  const [searchTimer, setSearchTimer] = useState(null);

  // Modal states
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [currentTutor, setCurrentTutor] = useState(null);
  const [requestDesc, setRequestDesc] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [successVisible, setSuccessVisible] = useState(false);

  // ── Budget: uses the same enum as backend ──
  const BUDGET_OPTIONS = [
    'R0-R50',
    'R50-R100',
    'R100-R150',
    'R150-R200',
    'R200-R250',
    'R250-R300',
    'R300-R400',
    'R400-R500',
    'R500-R750',
    'R750-R1000',
    '+R1000'
  ];
  const [requestBudget, setRequestBudget] = useState('R200-R250');

  const token = localStorage.getItem('token');

  // ═══════════════════════════════════════════════════════════
  // 1. FETCH ALL PROFILES ON MOUNT
  // ═══════════════════════════════════════════════════════════
  const fetchAllProfiles = useCallback(async () => {
    setInitialLoading(true);
    try {
      const res = await fetch('https://mykaddle-api.onrender.com/api/tutor/public');
      const tutors = await res.json();

      if (!Array.isArray(tutors) || tutors.length === 0) {
        setAllProfiles([]);
        setFilteredResults([]);
        return;
      }

      const cards = [];

      tutors.forEach(tutor => {
        const tutorData = {
          tutorId: tutor.tutorId || tutor._id,
          name: tutor.displayName || tutor.name || 'Unknown Tutor',
          qual: tutor.qualification || 'Qualification',
          institution: tutor.institution || '',
          desc: tutor.bio || '',
          avatar: tutor.profilePic || 'linear-gradient(135deg,#667eea,#764ba2)',
          likes: tutor.likeCount || tutor.likes || 0,
          members: tutor.totalMembers || tutor.members || 0,
          offeredSubjects: tutor.offeredSubjects || [],
        };

        if (tutor.offeredSubjects && Array.isArray(tutor.offeredSubjects)) {
          tutor.offeredSubjects.forEach(subject => {
            if (!subject.isActive) return;
            cards.push({
              ...tutorData,
              subjectId: subject.subjectId,
              offeredSubjectId: subject._id || subject.subjectId,
              subject: subject.subjectDisplayName || subject.subject,
              rate: subject.rate || 0,
              billingCycle: subject.billingCycle || 'monthly',
              currency: subject.currency || 'Rands',
              desc: subject.description || tutorData.desc,
              members: subject.tuteeCount || subject.members?.length || 0,
              isPrivate: false,
              cardId: `${subject.subjectId}-group`,
            });
          });
        }

        cards.push({
          ...tutorData,
          subjectId: tutor.tutorId || tutor._id,
          offeredSubjectId: tutor.offeredSubjects?.[0]?._id || tutor.tutorId,
          subject: tutor.institution || tutor.displayName,
          rate: tutor.offeredSubjects?.[0]?.rate || 0,
          billingCycle: 'weekly',
          currency: 'Rands',
          isPrivate: true,
          cardId: `${tutor.tutorId || tutor._id}-private`,
        });
      });

      setAllProfiles(cards);

      const initialLikes = {};
      cards.forEach(card => {
        initialLikes[card.cardId] = { liked: false, count: card.likes || 0 };
      });
      setLikesState(initialLikes);

      const groupCards = cards.filter(c => !c.isPrivate);
      setFilteredResults(groupCards);
    } catch (err) {
      console.error('Failed to fetch profiles:', err);
    } finally {
      setInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllProfiles();
  }, [fetchAllProfiles]);

  // ═══════════════════════════════════════════════════════════
  // 2. LOCAL FILTERING
  // ═══════════════════════════════════════════════════════════
  const filterLocally = useCallback((query, tab) => {
    if (!query.trim()) {
      return allProfiles.filter(card => {
        if (tab === 'group') return !card.isPrivate;
        return card.isPrivate;
      });
    }

    const q = query.toLowerCase().trim();

    return allProfiles.filter(card => {
      if (tab === 'group' && card.isPrivate) return false;
      if (tab === 'private' && !card.isPrivate) return false;

      const name = (card.name || '').toLowerCase();
      const subject = (card.subject || '').toLowerCase();
      const qual = (card.qual || '').toLowerCase();
      const desc = (card.desc || '').toLowerCase();
      const institution = (card.institution || '').toLowerCase();

      return (
        name.includes(q) ||
        subject.includes(q) ||
        qual.includes(q) ||
        desc.includes(q) ||
        institution.includes(q)
      );
    });
  }, [allProfiles]);

  // ═══════════════════════════════════════════════════════════
  // 3. SEARCH HANDLER
  // ═══════════════════════════════════════════════════════════
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    clearTimeout(searchTimer);

    if (value.trim().length > 0) {
      setSearching(true);
      const timer = setTimeout(() => {
        const filtered = filterLocally(value, activeTab);
        setFilteredResults(filtered);
        setSearching(false);
      }, 300);
      setSearchTimer(timer);
    } else {
      setSearching(false);
      const filtered = filterLocally('', activeTab);
      setFilteredResults(filtered);
    }
  };

  // ═══════════════════════════════════════════════════════════
  // 4. TAB CHANGE
  // ═══════════════════════════════════════════════════════════
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const filtered = filterLocally(searchQuery, tab);
    setFilteredResults(filtered);
  };

  // ── API CALLS ──
  const toggleLike = async (group, e) => {
    e.stopPropagation();
    const cardId = group.cardId || group.subjectId;
    const tutorId = group.tutorId;
    if (!tutorId) return;

    const currentState = likesState[cardId] || { liked: false, count: group.likes || 0 };
    const isLiked = currentState.liked;

    setLikesState(prev => ({
      ...prev,
      [cardId]: {
        liked: !isLiked,
        count: isLiked ? currentState.count - 1 : currentState.count + 1
      }
    }));

    try {
      const response = await fetch(
        `https://mykaddle-api.onrender.com/api/tutor/${tutorId}/like`,
        {
          method: isLiked ? 'DELETE' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setLikesState(prev => ({
          ...prev,
          [cardId]: { liked: !isLiked, count: data.likeCount }
        }));
      } else {
        setLikesState(prev => ({
          ...prev,
          [cardId]: { liked: isLiked, count: currentState.count }
        }));
      }
    } catch (err) {
      console.error('Like error:', err);
      setLikesState(prev => ({
        ...prev,
        [cardId]: { liked: isLiked, count: currentState.count }
      }));
    }
  };

  const handleJoinGroup = async (group, e) => {
    e.stopPropagation();
    const subjectId = group.subjectId;
    const cardId = group.cardId || subjectId;
    if (!subjectId) return;

    setJoiningGroups(prev => ({ ...prev, [cardId]: true }));

    try {
      const response = await fetch(
        `https://mykaddle-api.onrender.com/api/groups/join/${subjectId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );

      const data = await response.json();

      if (response.ok) {
        alert(`Welcome! You have a 30-hour free trial. Trial ends: ${new Date(data.trialEndsAt).toLocaleString()}`);
        navigate(`/portal/${subjectId}`);
      } else if (response.status === 400) {
        navigate(`/portal/${subjectId}`);
      } else if (response.status === 403 && data.capReached) {
        alert('You have reached the maximum of 3 subject groups. Leave or let a subscription expire before joining a new one.');
        setJoiningGroups(prev => ({ ...prev, [cardId]: false }));
      } else if (response.status === 403 && data.trialUsed) {
        setJoiningGroups(prev => ({ ...prev, [cardId]: false }));
        navigate('/payment/checkout', {
          state: {
            subjectId: group.subjectId,
            subjectName: group.subject,
            subscriptionAmount: group.rate || 0,
            billingCycle: group.billingCycle || 'monthly',
          }
        });
      } else {
        alert(data.message || 'Failed to join group.');
        setJoiningGroups(prev => ({ ...prev, [cardId]: false }));
      }
    } catch (err) {
      console.error('Join error:', err);
      alert('Something went wrong. Please try again.');
      setJoiningGroups(prev => ({ ...prev, [cardId]: false }));
    }
  };

  const openRequestModal = (tutor) => {
    setCurrentTutor(tutor);
    setRequestDesc('');
    setStartDate('');
    setEndDate('');
    setRequestBudget('R200-R250');
    setSuccessVisible(false);
    setRequestModalOpen(true);
  };

  const closeRequestModal = () => {
    setRequestModalOpen(false);
    setCurrentTutor(null);
  };

  // ── Budget selection handler ──
  const handleBudgetSelect = (budget) => {
    setRequestBudget(budget);
  };

  const submitRequest = async () => {
    if (!requestDesc.trim()) {
      alert('Please describe what you need help with.');
      return;
    }
    if (!startDate || !endDate) {
      alert('Please choose a start and end date.');
      return;
    }
    if (new Date(endDate) <= new Date(startDate)) {
      alert('End date must be after start date.');
      return;
    }

    try {
      const response = await fetch('https://mykaddle-api.onrender.com/api/tutee/request/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          tutorProfileId: currentTutor.tutorId,
          offeredSubjectId: currentTutor.offeredSubjectId,
          helpText: requestDesc,
          dateRange: {
            startDate: new Date(startDate).toISOString(),
            endDate: new Date(endDate).toISOString()
          },
          budget: requestBudget
        })
      });

      if (response.ok) {
        setSuccessVisible(true);
      } else {
        const errData = await response.json();
        alert(errData.error || 'Failed to send request. Please try again.');
      }
    } catch (err) {
      console.error('Request error:', err);
      alert('Something went wrong. Please try again.');
    }
  };

  const handleCardClick = (group) => {
    navigate(`/tutor/${group.tutorId}`, {
      state: {
        tutor: {
          id: group.tutorId,
          name: group.name,
          qualification: group.qual
        }
      }
    });
  };

  const visibleCount = filteredResults.length;

  return (
    <div className="ds-dashboard-container">
      <div className="ds-almighty-wrapper">
        {/* LEFT PANEL */}
        <div className="ds-discover-panel">
          <div className="ds-panel-top">
            <div className="ds-panel-top-left">
              <div className="ds-header-row">
                <button
                  className="ds-back-icon-btn"
                  onClick={() => navigate('/tutee/dashboard')}
                  aria-label="Go back"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="0.5em" height="1em" viewBox="0 0 12 24">
                    <path d="M0 0h12v24H0z" fill="none" />
                    <path fill="currentColor" fillRule="evenodd" d="M10 19.438L8.955 20.5l-7.666-7.79a1.02 1.02 0 0 1 0-1.42L8.955 3.5L10 4.563L2.682 12z" />
                  </svg>
                </button>
                <span className="ds-page-title">Tutor Discovery</span>
              </div>
              <p className="ds-sub-header">Find your tutor and join their group or make a Private Request</p>
            </div>
          </div>

          {/* TABS */}
          <div className="ds-tabs-wrapper">
            <div className="ds-tabs-container">
              <button className={`ds-tab-btn ${activeTab === 'group' ? 'active' : ''}`} onClick={() => handleTabChange('group')}>
                <span className="ds-tab-top">
                  <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 20 20">
                    <path d="M0 0h20v20H0z" fill="none" />
                    <g fill="currentColor" fillRule="evenodd" clipRule="evenodd">
                      <path d="M5 9a2 2 0 1 0 0-4a2 2 0 0 0 0 4m0 1a3 3 0 1 0 0-6a3 3 0 0 0 0 6" />
                      <path d="M3.854 8.896a.5.5 0 0 1 0 .708l-.338.337A3.47 3.47 0 0 0 2.5 12.394v1.856a.5.5 0 1 1-1 0v-1.856a4.47 4.47 0 0 1 1.309-3.16l.337-.338a.5.5 0 0 1 .708 0m11.792-.3a.5.5 0 0 0 0 .708l.338.337A3.47 3.47 0 0 1 17 12.094v2.156a.5.5 0 0 0 1 0v-2.156a4.47 4.47 0 0 0-1.309-3.16l-.337-.338a.5.5 0 0 0-.708 0" />
                      <path d="M14 9a2 2 0 1 1 0-4a2 2 0 0 1 0 4m0 1a3 3 0 1 1 0-6a3 3 0 0 1 0 6m-4.5 3.25a2.5 2.5 0 0 0-2.5 2.5v1.3a.5.5 0 0 1-1 0v-1.3a3.5 3.5 0 0 1 7 0v1.3a.5.5 0 1 1-1 0v-1.3a2.5 2.5 0 0 0-2.5-2.5" />
                      <path d="M9.5 11.75a2 2 0 1 0 0-4a2 2 0 0 0 0 4m0 1a3 3 0 1 0 0-6a3 3 0 0 0 0 6" />
                    </g>
                  </svg>
                  Join Group
                </span>
                <span className="ds-tab-sub">Group learning under one subject.</span>
              </button>

              <button className={`ds-tab-btn ${activeTab === 'private' ? 'active' : ''}`} onClick={() => handleTabChange('private')}>
                <span className="ds-tab-top">
                  <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 20 20">
                    <path d="M0 0h20v20H0z" fill="none" />
                    <g fill="none" stroke="currentColor" strokeLinecap="round">
                      <circle cx="9.5" cy="5.5" r="3" />
                      <path d="M15 16.5v-2c0-3.098-2.495-6-5.5-6S4 11.402 4 14.5v2" />
                    </g>
                  </svg>
                  1-1 Private Request
                </span>
                <span className="ds-tab-sub">Private tutoring, your terms.</span>
              </button>
            </div>
          </div>

          {/* SEARCH BAR */}
          <div className="ds-search-bar-wrap">
            <div className="ds-search-bar-container">
              <span className="ds-search-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                  <path d="M0 0h24v24H0z" fill="none" />
                  <g fill="none" fillRule="evenodd">
                    <path d="m12.593 23.258l-.011.002l-.071.035l-.02.004l-.014-.004l-.071-.035q-.016-.005-.024.005l-.004.01l-.017.428l.005.02l.01.013l.104.074l.015.004l.012-.004l.104-.074l.012-.016l.004-.017l-.017-.427q-.004-.016-.017-.018m.265-.113l-.013.002l-.185.093l-.01.01l-.003.011l.018.43l.005.012l.008.007l.201.093q.019.005.029-.008l.004-.014l-.034-.614q-.005-.018-.02-.022m-.715.002a.02.02 0 0 0-.027.006l-.006.014l-.034.614q.001.018.017.024l.015-.002l.201-.093l.01-.008l.004-.011l.017-.43l-.003-.012l-.01-.01z" />
                    <path fill="currentColor" d="M10.5 2a8.5 8.5 0 1 0 5.262 15.176l3.652 3.652a1 1 0 0 0 1.414-1.414l-3.652-3.652A8.5 8.5 0 0 0 10.5 2M4 10.5a6.5 6.5 0 1 1 13 0a6.5 6.5 0 0 1-13 0" />
                  </g>
                </svg>
              </span>
              <input type="text" className="ds-search-input" ref={searchInputRef} placeholder="Search for a tutor…" value={searchQuery} onChange={handleSearch} />
            </div>
          </div>

          {/* INFO BAR */}
          <div className="ds-info-bar">
            <div className="ds-info-bar-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                <path d="M0 0h24v24H0z" fill="none" />
                <path fill="#dcff32" d="M12 2.5c-3.81 0-6.5 2.743-6.5 6.119c0 1.536.632 2.572 1.425 3.56c.172.215.347.422.527.635l.096.112c.21.25.427.508.63.774c.404.531.783 1.128.995 1.834a.75.75 0 0 1-1.436.432c-.138-.46-.397-.89-.753-1.357a18 18 0 0 0-.582-.714l-.092-.11c-.18-.212-.37-.436-.555-.667C4.87 12.016 4 10.651 4 8.618C4 4.363 7.415 1 12 1s8 3.362 8 7.619c0 2.032-.87 3.397-1.755 4.5c-.185.23-.375.454-.555.667l-.092.109c-.21.248-.405.481-.582.714c-.356.467-.615.898-.753 1.357a.751.751 0 0 1-1.437-.432c.213-.706.592-1.303.997-1.834c.202-.266.419-.524.63-.774l.095-.112c.18-.213.355-.42.527-.634c.793-.99 1.425-2.025 1.425-3.561C18.5 5.243 15.81 2.5 12 2.5M8.75 18h6.5a.75.75 0 0 1 0 1.5h-6.5a.75.75 0 0 1 0-1.5m.75 3.75a.75.75 0 0 1 .75-.75h3.5a.75.75 0 0 1 0 1.5h-3.5a.75.75 0 0 1-.75-.75" />
              </svg>
            </div>
            <p className="ds-info-bar-text">
              You get <strong>free 30hr trial</strong> for new group subjects upon joining.{' '}
              <strong>1-1 Private</strong> gets you <strong>30mins</strong> free after agreement — see{' '}
              <strong>'Guide'</strong> in support.
            </p>
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="ds-cards-panel">
          <div className="ds-cards-header">
            <span className="ds-cards-count">
              Showing <strong>{visibleCount}</strong> tutor{visibleCount !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="ds-cards-scroll" id="dsCardsScroll">
            {initialLoading && (
              <div className="ds-search-loader visible">
                <div className="ds-loader-anim">
                  <div className="ds-loaderMiniContainer">
                    <div className="ds-barContainer">
                      <span className="ds-bar"></span>
                      <span className="ds-bar ds-bar2"></span>
                    </div>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 101 114" className="ds-svgIcon">
                      <circle strokeWidth="7" stroke="#8abf00" transform="rotate(36.0692 46.1726 46.1727)" r="29.5497" cy="46.1727" cx="46.1726" fill="rgba(180,230,0,0.15)"></circle>
                      <line strokeWidth="7" stroke="#8abf00" y2="111.784" x2="97.7088" y1="67.7837" x1="61.7089"></line>
                    </svg>
                  </div>
                </div>
                <span className="ds-loader-label">Loading tutors…</span>
              </div>
            )}

            {!initialLoading && searching && (
              <div className="ds-search-loader visible">
                <div className="ds-loader-anim">
                  <div className="ds-loaderMiniContainer">
                    <div className="ds-barContainer">
                      <span className="ds-bar"></span>
                      <span className="ds-bar ds-bar2"></span>
                    </div>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 101 114" className="ds-svgIcon">
                      <circle strokeWidth="7" stroke="#8abf00" transform="rotate(36.0692 46.1726 46.1727)" r="29.5497" cy="46.1727" cx="46.1726" fill="rgba(180,230,0,0.15)"></circle>
                      <line strokeWidth="7" stroke="#8abf00" y2="111.784" x2="97.7088" y1="67.7837" x1="61.7089"></line>
                    </svg>
                  </div>
                </div>
                <span className="ds-loader-label">Filtering tutors…</span>
              </div>
            )}

            {!initialLoading && !searching &&
              filteredResults.map((group, index) => {
                const cardId = group.cardId || group.subjectId || index;
                const cardLikes = likesState[cardId] || { liked: false, count: group.likes || 0 };
                const isJoining = joiningGroups[cardId] || false;
                const isPrivate = group.isPrivate;
                const hdr = isPrivate
                  ? (group.institution || group.name || 'Private Tutor')
                  : (group.subject || 'Group');
                const mode = isPrivate ? 'mode-private' : 'mode-group';

                return (
                  <div key={cardId} className="ds-tutor-card" onClick={() => handleCardClick(group)}>
                    <div className={`ds-card-group-header ds-${mode}`}>{hdr}</div>
                    <div className="ds-inner-card">
                      <div className="ds-card-content">
                        <div className="ds-c-header">
                          <div className="ds-profile-pic-placeholder" style={{ background: typeof group.avatar === 'string' && group.avatar.startsWith('http') ? `url(${group.avatar}) center/cover` : group.avatar || 'linear-gradient(135deg,#667eea,#764ba2)' }}></div>
                          <div className="ds-name-qual">
                            <p className="ds-c-name">{group.name || 'Tutor'}</p>
                            <p className="ds-qualification">{group.qual || 'Qualification'}</p>
                          </div>
                        </div>
                        <p className="ds-subject-description">{group.desc || 'No description available'}</p>
                        <div className="ds-footer">
                          <div className="ds-footer-left">
                            <div className="ds-likes">
                              <button className="ds-like-btn" onClick={(e) => toggleLike(group, e)}>
                                <svg className={`ds-like-icon ${cardLikes.liked ? 'liked' : 'unliked'}`} viewBox="0 0 48 48" fill="none">
                                  <path fill="none" d="M0 0h48v48H0z" />
                                  <path fill="currentColor" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M15 8C8.925 8 4 12.925 4 19c0 11 13 21 20 23.326C31 40 44 30 44 19c0-6.075-4.925-11-11-11c-3.72 0-7.01 1.847-9 4.674A10.99 10.99 0 0 0 15 8" fillOpacity={cardLikes.liked ? '1' : '0.18'} />
                                </svg>
                              </button>
                              <span className="ds-like-count">{cardLikes.count}</span>
                            </div>
                            {!isPrivate && (
                              <div className="ds-tutees">
                                <svg className="ds-tutees-icon" viewBox="0 0 20 20">
                                  <path d="M0 0h20v20H0z" fill="none" />
                                  <g fill="currentColor" fillRule="evenodd" clipRule="evenodd">
                                    <path d="M5 9a2 2 0 1 0 0-4a2 2 0 0 0 0 4m0 1a3 3 0 1 0 0-6a3 3 0 0 0 0 6" />
                                    <path d="M3.854 8.896a.5.5 0 0 1 0 .708l-.338.337A3.47 3.47 0 0 0 2.5 12.394v1.856a.5.5 0 1 1-1 0v-1.856a4.47 4.47 0 0 1 1.309-3.16l.337-.338a.5.5 0 0 1 .708 0m11.792-.3a.5.5 0 0 0 0 .708l.338.337A3.47 3.47 0 0 1 17 12.094v2.156a.5.5 0 0 0 1 0v-2.156a4.47 4.47 0 0 0-1.309-3.16l-.337-.338a.5.5 0 0 0-.708 0" />
                                    <path d="M14 9a2 2 0 1 1 0-4a2 2 0 0 1 0 4m0 1a3 3 0 1 1 0-6a3 3 0 0 1 0 6m-4.5 3.25a2.5 2.5 0 0 0-2.5 2.5v1.3a.5.5 0 0 1-1 0v-1.3a3.5 3.5 0 0 1 7 0v1.3a.5.5 0 1 1-1 0v-1.3a2.5 2.5 0 0 0-2.5-2.5" />
                                    <path d="M9.5 11.75a2 2 0 1 0 0-4a2 2 0 0 0 0 4m0 1a3 3 0 1 0 0-6a3 3 0 0 0 0 6" />
                                  </g>
                                </svg>
                                <span className="ds-tutees-count">{group.members || 0}</span>
                              </div>
                            )}
                          </div>
                          <div className="ds-card-actions-right">
                            {!isPrivate && (
                              <span className="ds-rate">
                                {group.currency === 'Pula' ? 'P' : 'R'}{group.rate || 0}/
                                {group.billingCycle === 'weekly' ? 'wk' : 'mo'}
                              </span>
                            )}
                            {isPrivate ? (
                              <button className={`ds-join-btn ds-${mode}`} onClick={(e) => { e.stopPropagation(); openRequestModal(group); }}>
                                Make Request
                              </button>
                            ) : (
                              <button className={`ds-join-btn ds-${mode}`} onClick={(e) => handleJoinGroup(group, e)} disabled={isJoining}>
                                {isJoining ? 'Joining...' : 'Join Group'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

            {!initialLoading && !searching && visibleCount === 0 && (
              <div className="ds-empty-state">
                <div className="ds-empty-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </div>
                <div className="ds-empty-text">No tutors found.<br/>Try a different search or tab.</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── REQUEST MODAL (rectangular, no scroll) ── */}
      {requestModalOpen && (
        <div className={`ds-modal-backdrop ${requestModalOpen ? 'open' : ''}`}>
          <div className="ds-modal ds-modal-rect">
            <button className="ds-modal-close" onClick={closeRequestModal}>✕</button>

            {!successVisible ? (
              <>
                {/* Header row: icon + title/sub side by side */}
                <div className="dsrq-header">
                  <div className="ds-modal-icon-ring ds-lime dsrq-header-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 48 48">
                      <path d="M0 0h48v48H0z" fill="none" />
                      <defs>
                        <mask id="SVGyzgoLcxy">
                          <g fill="none" stroke="#fff" strokeLinejoin="round" strokeWidth="4">
                            <path d="M44 24v16.818C44 42.023 43.105 43 42 43H6c-1.105 0-2-.977-2-2.182V24l20 13z" />
                            <path strokeLinecap="round" d="m4 23.784l10-6.892m30 6.892l-10-6.892" />
                            <path fill="#555" d="M34 5H14v24.415a2 2 0 0 0 .91 1.677l8 5.2a2 2 0 0 0 2.18 0l8-5.2a2 2 0 0 0 .91-1.677z" />
                            <path strokeLinecap="round" d="M20 13h4m-4 6h8" />
                          </g>
                        </mask>
                      </defs>
                      <path fill="currentColor" d="M0 0h48v48H0z" mask="url(#SVGyzgoLcxy)" />
                    </svg>
                  </div>
                  <div className="dsrq-header-text">
                    <div className="ds-modal-title">Make a Private Request</div>
                    <div className="ds-modal-sub dsrq-sub-tight">Describe what you need, your preferred dates, and your weekly budget.</div>
                  </div>
                  <div className="ds-preview-chip dsrq-header-chip">
                    <div className="ds-preview-info">
                      <span className="ds-preview-name">{currentTutor?.name || '—'}</span>
                      <span className="ds-preview-sub">{currentTutor?.institution || '—'}</span>
                    </div>
                  </div>
                </div>

                {/* Two-column body — fits without scrolling */}
                <div className="dsrq-body-grid">
                  {/* LEFT: description */}
                  <div className="dsrq-col dsrq-col-left">
                    <div className="ds-modal-field dsrq-desc-field">
                      <div className="ds-modal-label-row">
                        <label className="ds-modal-label">Request description</label>
                        <span className={`ds-modal-char-count ${requestDesc.length >= 260 ? 'ds-warn' : ''}`}>{requestDesc.length} / 300</span>
                      </div>
                      <textarea
                        className="ds-modal-textarea dsrq-textarea"
                        maxLength="300"
                        placeholder="Describe what you need help with…"
                        value={requestDesc}
                        onChange={(e) => setRequestDesc(e.target.value)}
                      />
                    </div>
                    <div className="ds-intel-message dsrq-tip"><strong>Tip:</strong> The more specific you are, the easier it is for the tutor to assess whether they can help you.</div>
                  </div>

                  {/* RIGHT: dates + budget */}
                  <div className="dsrq-col dsrq-col-right">
                    <div className="ds-modal-field">
                      <label className="ds-modal-label">Preferred tutoring period</label>
                      <div className="ds-date-range-row dsrq-date-stack">
                        <input type="date" className="ds-modal-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                        <span className="ds-date-sep dsrq-date-sep-vert">→</span>
                        <input type="date" className="ds-modal-input" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                      </div>
                    </div>

                    <div className="ds-modal-field">
                      <label className="ds-modal-label">Weekly budget</label>
                      <div className="ds-budget-pills dsrq-budget-pills">
                        {BUDGET_OPTIONS.map((option) => (
                          <button
                            key={option}
                            type="button"
                            className={`ds-budget-pill ${requestBudget === option ? 'ds-budget-pill-active' : ''}`}
                            onClick={() => handleBudgetSelect(option)}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="ds-modal-actions dsrq-actions">
                  <button className="ds-btn-cancel" onClick={closeRequestModal}>Cancel</button>
                  <button className="ds-btn-confirm-lime" onClick={submitRequest}>Send Request</button>
                </div>
              </>
            ) : (
              <div className="mkreq-success-overlay visible">
                <div className="mkreq-success-badge">
                  <div className="mkreq-success-badge-pulse"></div>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <div className="mkreq-success-heading">Request sent!</div>
                <div className="mkreq-success-message">
                  Your request to <strong>{currentTutor?.name}</strong> is on its way. You'll be notified as soon as they respond.
                </div>
                <button className="mkreq-success-btn" onClick={closeRequestModal}>Done</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TutorDiscovery;